<template>
  <div class="wrapper">
    <div class="options">
      <div class="center">
        My character is a level
        <input type="number" v-model="level" id="level" @change="recalculateHp">
        <select v-model="characterClass" @change="recalculateHp">
          <option value="barbarian">Barbarian</option>
          <option value="bard">Bard</option>
          <option value="cleric">Cleric</option>
          <option value="druid">Druid</option>
          <option value="fighter">Fighter</option>
          <option value="monk">Monk</option>
          <option value="paladin">Paladin</option>
          <option value="ranger">Ranger</option>
          <option value="rogue">Rogue</option>
          <option value="sorcerer">Sorcerer</option>
          <option value="warlock">Warlock</option>
          <option value="wizard">Wizard</option>
        </select>
      </div>

      <div class="center">
        Which has a CON
        <select v-model="conSelector" @change="changeConSelector">
          <option value="modifier">modifier</option>
          <option value="score">score</option>
        </select>
        of
        <input type="number" v-model="conValue" @change="recalculateHp">
      </div>

      <div class="hpModifierOptions center">
        <label for="tough">
          ...and has the Tough feat
        </label>
        <input type="checkbox" name="tough" id="tough" @change="recalculateHp" v-model="hasToughFeat">
      </div>
      <div class="hpModifierOptions center">
        <label for="hillDwarf">
          ...and is a Hill Dwarf
        </label>
        <input type="checkbox" name="hillDwarf" id="hillDwarf" @change="recalculateHp" v-model="isHillDwarf">
      </div>
      <div class="hpModifierOptions center" v-if="characterClass == 'sorcerer'">
        <label for="draconicSorcerer">
          ...and is a Draconic Sorcerer
        </label>
        <input type="checkbox" name="draconicSorcerer" id="draconicSorcerer" @change="recalculateHp" v-model="isDraconicSorcerer">
      </div>
    </div>

    <div class="results">
      <div class="grid twoColumns">
        <div class="center">
          <input type="radio" value="average" id="average" @change="recalculateHp" v-model="hpMethod">
          <label for="average">
            Average
          </label>
        </div>
        <div class="center">
          <input type="radio" value="rolled" id="rolled" @change="recalculateHp" v-model="hpMethod">
          <label for="rolled">
            Rolled
          </label>
        </div>
      </div>

      <div class="totalHp">
        <strong>{{ totalHp }}</strong>
      </div>

      <div class="center">
        <div class="hitDice">
          <strong>Hit dice:</strong>
          d{{ hitDice }}
        </div>
        <div class="conModifier">
          <strong>CON modifier:</strong>
          {{ conModifier }}
        </div>
        <div class="level1HP">
          <strong>HP at level 1:</strong>
          <span v-tooltip="hpTooltip(hitDice, conModifier, 'hit dice')">
            {{ hitDice + conModifier }}
          </span>
        </div>

        <div v-if="level > 1">
          <strong>HP at subsequent levels:</strong>
          <span v-if="hpMethod == 'average'" v-tooltip="hpTooltip(averageHp(), conModifier, 'average')">
            {{ averageHp() + conModifier }}
          </span>
          <span v-else>
            <span v-for="(die, i) in rolledDice" :key="i" v-tooltip="hpTooltip(die, conModifier, 'rolled')">
              {{ (die + conModifier) + (((rolledDice.length - 1) > i) ? ',' : '') }}
            </span>
          </span>
        </div>

        <div v-if="hpMethod == 'rolled'" id="rollAgain">
          <a href="#" @click="reroll">Roll again</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  function getHitDice(characterClass) {
    switch (characterClass) {
      case 'barbarian':
        return 12;

      case 'fighter':
      case 'ranger':
      case 'paladin':
        return 10;

      case 'wizard':
      case 'sorcerer':
        return 6;

      default:
        return 8;
    }
  }

  function conFormula(score) {
    return Math.floor((score - 10) / 2);
  }

  export default {
    name: 'HpCalculator',
    data() {
      return {
        level: 3,
        characterClass: 'cleric',
        hitDice: 12,
        conSelector: 'modifier',
        conValue: 1,
        conModifier: 1,
        hpMethod: 'average',
        rolledDice: [],
        totalHp: 1,
        hasToughFeat: false,
        isHillDwarf: false,
        isDraconicSorcerer: false
      }
    },
    created() {
      this.recalculateHp();
    },
    methods: {
      changeConSelector() {
        if (this.conSelector == 'modifier') {
          this.conValue = conFormula(this.conValue);
        } else {
          this.conValue = this.conValue * 2 + 10;
        }
        this.recalculateHp();
      },
      calculateConModifier() {
        if (this.conSelector == 'modifier') {
          this.conModifier = parseInt(this.conValue);
        } else {
          this.conModifier = conFormula(this.conValue);
        }
      },
      averageHp() {
        return (this.hitDice / 2) + 1
      },
      hpTooltip(base, modifier, baseText) {
        let tooltip = '';
        if (modifier < 0) {
          tooltip += `${base} - ${Math.abs(modifier)}`;
        } else {
          tooltip += `${base} + ${modifier}`;
        }
        return tooltip + `<br />(${baseText} + CON mod)`;
      },
      reroll(e) {
        e.preventDefault();
        this.rolledDice = [];
        this.recalculateHp();
      },
      recalculateHp() {
        if (this.level < 1) {
          this.level = 1;
        } else if (this.level > 20) {
          this.level = 20;
        }

        const levelsBeyondOne = this.level - 1;
        var hp = 0;

        // Calculate CON modifier
        this.calculateConModifier();

        // Hit dice depends on class
        this.hitDice = getHitDice(this.characterClass);

        // Level 1
        hp += this.hitDice + this.conModifier;

        // Subsequent levels
        if (this.hpMethod == 'average') {
          // We're using average hp
          hp += levelsBeyondOne * (this.averageHp() + this.conModifier);
        } else {
          // We're rolling
          if (this.rolledDice.length > levelsBeyondOne) {
            this.rolledDice = this.rolledDice.slice(0, levelsBeyondOne);
          }
          while (this.rolledDice.length < levelsBeyondOne) {
            this.rolledDice.push(
              (Math.floor(Math.random() * this.hitDice)) + 1
            );
          }

          if (this.rolledDice.length > 0) {
            hp += this.rolledDice.reduce((sum, x) => sum + x) + (levelsBeyondOne * this.conModifier);
          }
        }

        if (this.isHillDwarf) {
          hp += parseInt(this.level);
        }

        if (this.isDraconicSorcerer && this.characterClass == 'sorcerer') {
          hp += parseInt(this.level);
        }

        if (this.hasToughFeat) {
          hp += parseInt(this.level) * 2;
        }

        this.totalHp = hp;
      }
    }
  }
</script>

<style scoped>
.wrapper {
  max-width: 600px;
  margin: 0 auto;
}

.options {
  font-size: 18px;
  align-self: center;
}

input[type='number'], select {
  font-family: 'Source Sans Pro';
  background-color: transparent;
  border: none;
  border-bottom: 1px solid #333;
  font-size: 18px;
  text-align: center;
  -webkit-appearance: none;
  -webkit-border-radius: 0px !important;
  -moz-border-radius: 0px !important;
  border-radius: 0px !important;
}

input[type='number'] {
  width: 40px;
}

select {
  padding-bottom: 1px;
  padding-right: 25px;
  background-image: url("data:image/svg+xml;utf8,<svg version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='24' height='24' viewBox='0 0 24 24'><path d='M7.406 7.828l4.594 4.594 4.594-4.594 1.406 1.406-6 6-6-6z'></path></svg>");
  background-position: 100% 50%;
  background-repeat: no-repeat;
}

input[type='number'] + select {
  margin-left: 5px;
}

.hpModifierOptions {
  margin-top: 15px;
}

.hpModifierOptions + .hpModifierOptions {
  margin-top: 5px;
}

.results {
  margin-top: 50px;
  padding: 15px;
  border-top: 4px solid orange;
  border-bottom: 4px solid orange;
  background-color: #fdf1dc;
  color: #7a200d;
}

.results a {
  color: #7a200d;
  font-weight: bold;
}

.totalHp {
  font-size: 25px;
  text-align: center;
}

#rollAgain {
  margin-top: 10px;
}

.grid { display: grid; }
.twoColumns { grid-template-columns: 1fr 1fr; }

.left { text-align: left; }
.center { text-align: center; }
.right { text-align: right; }
</style>
